import type { MetricQuery } from "~/models/metric-query";
import { flatMap } from "~/tools/flat-map";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { isError } from "~/tools/is-error";
import { isPresent } from "~/tools/is-present";
import { isShallowEqual } from "~/tools/is-shallow-equal";
import { pick } from "~/tools/pick";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";
import { useMemo } from "~/tools/use-memo";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { calculateQueryRangeStep } from "../../metrics/utils";
import { queryRangePromQL } from "../../queries/metrics/batch";
import { type UseFullQueryOptions, useQueries } from "../../queries/react-query";
import type { TimeSeries } from "../TimeSeriesChart/time-series";

import { QUERY_NAMESPACE } from "./_query-namespace";

function useQueryTimeSeries({
	chartWidth,
	timeRange,
	metricQueries,
}: {
	chartWidth: number;
	timeRange: TimeRange;
	metricQueries: ReadonlyArray<MetricQuery>;
}) {
	const defaultStep = calculateQueryRangeStep({ timeRange, chartWidth });

	const results = useQueries(
		metricQueries.map((metricQuery) => {
			const step = metricQuery.step ?? defaultStep;

			return {
				enabled: Boolean(step),
				namespace: QUERY_NAMESPACE,
				keepPreviousData: true,
				refetchOnMount: false,
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,

				...queryRangePromQL({
					batch: true,
					query: metricQuery.promql,
					step,
					timeRange,
				}),
			} satisfies UseFullQueryOptions;
		}),
	).map(pick(["data", "error"]));

	return useMemo(
		() => {
			if (metricQueries.length === 0) {
				return RemoteData.loading();
			}

			const error = results.map(prop("error")).find(isPresent);

			if (isError(error)) {
				return RemoteData.failure(error);
			}

			if (error != null) {
				return RemoteData.failure(new Error("Unknown error"));
			}

			return RemoteData.all(results.map(({ data = RemoteData.loading() }) => data))
				.transform(
					flatMap((series, index) => {
						const metricQuery = metricQueries.at(index);

						return series.map(
							(matrix): TimeSeries => ({
								...matrix,
								alias: metricQuery?.label,
								componentIdentifier: metricQuery?.componentIdentifier,
							}),
						);
					}),
				)
				.transformFailure((message) => new Error(message));
		},
		[results, metricQueries],
		[isShallowEqual, isDeepEqual],
	);
}

export { useQueryTimeSeries };
